<template>
  <MainLayout>
    <template #header>
      <div class="mb-6">
        <h1 class="text-2xl font-bold">Feature flags</h1>
        <p class="text-gray-600">
          Toggle Worklist + product features per environment. Disabled features
          appear greyed out in the destination environment's UI.
        </p>
      </div>
    </template>

    <ConfirmDialog />
    <div class="bg-white rounded-lg shadow p-4">
      <div class="flex items-center gap-3 mb-4">
        <span class="p-input-icon-left flex-1 max-w-md">
          <i class="pi pi-search" />
          <InputText
            v-model="search"
            placeholder="Search by organisation name or subdomain"
            class="w-full"
          />
        </span>
        <span class="text-sm text-gray-500">
          {{ filteredOrganisations.length }} / {{ organisations.length }}
        </span>
      </div>

      <div v-if="loading" class="text-center py-6">
        <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i>
      </div>

      <div v-else-if="filteredOrganisations.length" class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="bg-gray-50 border-b">
              <th
                rowspan="2"
                class="text-left p-3 sticky left-0 bg-gray-50 z-10 align-bottom"
              >
                Organisation
              </th>
              <th
                v-for="group in flagSections"
                :key="group.name"
                :colspan="group.flags.length"
                class="p-3 text-center font-semibold whitespace-nowrap border-l text-gray-700"
              >
                {{ group.name }}
              </th>
            </tr>
            <tr class="bg-gray-50 border-b">
              <template v-for="group in flagSections" :key="group.name">
                <th
                  v-for="(flag, i) in group.flags"
                  :key="flag.key"
                  class="p-3 text-center font-medium whitespace-nowrap text-gray-500"
                  :class="{ 'border-l': i === 0 }"
                  :title="flag.description"
                >
                  <span :class="{ 'cursor-help': flag.description }">{{ flag.label }}</span>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="org in filteredOrganisations" :key="org.id" class="border-b hover:bg-gray-50">
              <td class="p-3 sticky left-0 bg-white z-10">
                <div class="font-medium">{{ org.name }}</div>
                <div class="text-xs text-gray-500 font-mono">{{ org.subdomain }}</div>
              </td>
              <template v-for="group in flagSections" :key="group.name">
                <td
                  v-for="(flag, i) in group.flags"
                  :key="flag.key"
                  class="p-3 text-center"
                  :class="{ 'border-l': i === 0 }"
                >
                  <InputSwitch
                    :modelValue="org.flags[flag.key]"
                    @update:modelValue="(val) => onToggle(org, flag.key, val)"
                    :loading="org._saving === flag.key"
                  />
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-center py-6 text-gray-500">
        {{ organisations.length ? 'No organisations match your search.' : 'No organisations found.' }}
      </div>
    </div>
  </MainLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useAuthStore } from '../store/auth';
import MainLayout from '../components/MainLayout.vue';
import InputSwitch from 'primevue/inputswitch';
import InputText from 'primevue/inputtext';
import ConfirmDialog from 'primevue/confirmdialog';

const toast = useToast();
const confirm = useConfirm();
const loading = ref(false);
const flags = ref([]);
const sections = ref([]);
const organisations = ref([]);
const search = ref('');

// Columns grouped under their section header, preserving section order.
const flagSections = computed(() =>
  sections.value
    .map((name) => ({
      name,
      flags: flags.value.filter((f) => f.section === name)
    }))
    .filter((group) => group.flags.length)
);

const filteredOrganisations = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return organisations.value;
  return organisations.value.filter((org) => {
    const name = (org.name || '').toLowerCase();
    const sub  = (org.subdomain || '').toLowerCase();
    return name.includes(term) || sub.includes(term);
  });
});

const load = async () => {
  loading.value = true;
  try {
    const res = await axios.get('/api/v1/feature_flags');
    flags.value = res.data?.flags || [];
    sections.value = res.data?.sections || [];
    organisations.value = (res.data?.organisations || []).map((o) => ({
      ...o,
      _saving: null
    }));
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load feature flags', life: 3000 });
  } finally {
    loading.value = false;
  }
};

const applyToggle = async (org, key, value) => {
  const previous = org.flags[key];
  org.flags[key] = value;
  org._saving = key;
  try {
    await axios.patch(`/api/v1/organisations/${org.id}/features`, {
      features: { [key]: value }
    });
    if (key === 'is_organisation') {
      // The backend applied the full entity-type default profile; reload to
      // reflect every flag it reset, not just this toggle.
      await load();
      toast.add({
        severity: 'success',
        summary: 'Defaults applied',
        detail: `${org.name}: ${value ? 'organisation' : 'expert'} defaults`,
        life: 2500
      });
    } else {
      toast.add({
        severity: 'success',
        summary: 'Updated',
        detail: `${org.name}: ${key} = ${value}`,
        life: 2000
      });
    }
  } catch (e) {
    org.flags[key] = previous;
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e.response?.data?.error || 'Failed to update flag',
      life: 3000
    });
  } finally {
    org._saving = null;
  }
};

const onToggle = (org, key, value) => {
  // Changing the entity type is a full reset of every flag to that type's
  // defaults, so confirm before applying.
  if (key === 'is_organisation') {
    confirm.require({
      message: `Set ${org.name} to ${value ? 'organisation (firm)' : 'expert'}? `
        + `This resets all feature flags to the ${value ? 'organisation' : 'expert'} defaults.`,
      header: 'Apply entity-type defaults',
      icon: 'pi pi-exclamation-triangle',
      accept: () => applyToggle(org, key, value),
      reject: () => {}
    });
    return;
  }
  applyToggle(org, key, value);
};

const authStore = useAuthStore();

// Refetch when the exchange instance (core/tula) changes so the toggle takes
// effect without a full page reload.
watch(() => authStore.exchangeInstance, () => {
  load();
});

onMounted(load);
</script>
